/**
 * @fileoverview The realtime ORIGIN of a write (APPROVALS-STORE-ROOMS-001 U7, owner ruling R-UAT2-26): the ONE
 * registered source of the session's live realtime connection id, read by the request interceptor at request time
 * and stamped as `CLIENT_HEADERS.REALTIME_ORIGIN` on every WRITE the sanctioned transport sends.
 *
 * `@puffin-core/realtime-context`'s `<RealtimeClientProvider>` registers `() => client.connectionId()` once the
 * session's socket is open and withdraws it on unmount; nothing else should. The source is asked per request, so a
 * reconnect's new id is what the next write carries, and a source answering `null` (no live socket right now) stamps
 * nothing. The server's emit for that write comes back stamped with the same id, and only that connection — never
 * another tab of the same user — skips it as its own echo.
 *
 * Reach it by the `./realtime-origin` subpath from a shared package (the provider does), so a suite that doubles the
 * api-client BARREL for its own calls still leaves the provider's registration real.
 *
 * The STAMP is one header helper, `realtimeOriginHeaders(method)` (U10, the finish gate's prompt 2): this package's
 * own interceptor composes it, and so does every transport the owner ruled to stay outside this package (pos-web's
 * `ApiClientManager` and its two cross-app clients, `bespokeHttpTransportDebt.cjs`), beside `clientContractHeaders` —
 * so exactly one place knows what a write is and what it carries. The `realtimeOriginOnEveryTransport` contract
 * holds that line over a census of every `axios.create()` outside this package.
 *
 * @module @puffin-core/api-client/realtimeOrigin
 */

import { CLIENT_HEADERS } from '@puffin-core/client-contract';

const SOURCE_REFUSAL =
  'setRealtimeOriginSource: the source must be a function answering the live connection id, or null';

/** The methods whose request is a WRITE — the only requests that can cause an emit, so the only ones that name an origin. */
export const REALTIME_ORIGIN_WRITE_METHODS = Object.freeze(['post', 'put', 'patch', 'delete']);

/** @type {(() => (string|null|undefined))|null} */
let source = null;

/**
 * Register the ONE source of the session's live realtime connection id, or withdraw it with `null`.
 *
 * @param {(() => (string|null|undefined))|null} next
 */
export function setRealtimeOriginSource(next) {
  if (next !== null && typeof next !== 'function') {
    throw new TypeError(SOURCE_REFUSAL);
  }
  source = next;
}

/**
 * The session's live realtime connection id right now, or `null` when no source is registered or the source
 * answers none.
 *
 * @returns {string|null}
 */
export function readRealtimeOrigin() {
  if (!source) return null;
  const id = source();
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * The headers a request must carry to name its realtime origin: `{ [CLIENT_HEADERS.REALTIME_ORIGIN]: id }` for a
 * WRITE while the registered source answers a live id, `{}` for a read, an unnamed method, or a write while no live
 * socket is known. Composed into a request's headers at REQUEST time (`Object.assign(config.headers, …)`), never
 * captured at construction — a reconnect's new id is what the next write carries.
 *
 * @param {string} [method] - The request's method, in any case
 * @returns {Object<string, string>}
 */
export function realtimeOriginHeaders(method) {
  if (!REALTIME_ORIGIN_WRITE_METHODS.includes(String(method ?? '').toLowerCase())) return {};
  const origin = readRealtimeOrigin();
  return origin ? { [CLIENT_HEADERS.REALTIME_ORIGIN]: origin } : {};
}
